import { computed, watchEffect } from 'vue'
import { useUserStore } from '../stores/user'

/**
 * Advisor white-label branding.
 * When an advisor enables branding, override the app's teal accent with their
 * chosen color and expose their logo for the chrome. When off, the inline
 * overrides are removed so the default themed (light/dark) tokens take over.
 */

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const h = hex.replace('#', '').trim()
  const n = h.length === 3 ? h.split('').map(c => c + c).join('') : h
  if (!/^[0-9a-fA-F]{6}$/.test(n)) return null
  const i = parseInt(n, 16)
  return { r: (i >> 16) & 255, g: (i >> 8) & 255, b: i & 255 }
}
function lighten(hex: string, amt: number): string {
  const c = hexToRgb(hex); if (!c) return hex
  const f = (x: number) => Math.max(0, Math.min(255, Math.round(x + (255 - x) * amt)))
  return `rgb(${f(c.r)}, ${f(c.g)}, ${f(c.b)})`
}
function rgba(hex: string, a: number): string {
  const c = hexToRgb(hex); if (!c) return hex
  return `rgba(${c.r}, ${c.g}, ${c.b}, ${a})`
}

const OVERRIDE_VARS = ['--primary', '--primary-hover', '--primary-strong', '--primary-subtle', '--live']

export function useBranding() {
  const userStore = useUserStore()
  const advisor = computed<any>(() => userStore.user?.advisor ?? null)
  const brandingEnabled = computed(() => !!(advisor.value?.brandingEnabled && advisor.value?.brandColor && hexToRgb(advisor.value.brandColor)))
  const brandLogoUrl = computed<string | null>(() => (advisor.value?.brandingEnabled && advisor.value?.logoUrl) ? advisor.value.logoUrl : null)

  watchEffect(() => {
    const root = document.documentElement
    if (brandingEnabled.value) {
      const c = advisor.value.brandColor as string
      root.style.setProperty('--primary', c)
      root.style.setProperty('--primary-hover', lighten(c, 0.12))
      root.style.setProperty('--primary-strong', lighten(c, 0.12))
      root.style.setProperty('--primary-subtle', rgba(c, 0.10))
      root.style.setProperty('--live', c)
    } else {
      OVERRIDE_VARS.forEach(v => root.style.removeProperty(v))
    }
  })

  return { brandingEnabled, brandLogoUrl }
}
